import type { Clock, TimerHandle } from "./clock";
import { FOLD_DELAY_MS, HOLD_MS } from "./pay-timeline";
import type { PayPhase, PayStatus } from "./pay-timeline";
import { PAY_STATUS_LABEL } from "./motion";

/**
 * The status the primary button becomes once pressed, whoever is moving
 * behind it: the engine's payment (pay-timeline.ts) or a vendor's prepared
 * checkout (`@libs/buy-on-behalf-client`). Every renderer draws a line the
 * same way — its words, the mark beside them, and the fold around it — so a
 * provider only decides what the words and the mark are.
 *
 *   spinner  something is still moving
 *   done     the engine issued the tickets ("You're going", in green)
 *   ready    a prepared checkout stopped before Pay: finished, not paid
 *   stopped  nothing more will happen without the buyer
 *
 * Only the engine's payment is ever `done`: a checkout prepared elsewhere
 * never claims tickets it has not bought.
 */
export type StatusMark = "spinner" | "done" | "ready" | "stopped";
export type StatusLine = { words: string; mark: StatusMark };

/** The engine payment's step, as a line. */
export function payStatusLine(status: PayStatus): StatusLine {
  return {
    words: PAY_STATUS_LABEL[status],
    mark: status === "done" ? "done" : "spinner",
  };
}

export type StepView<K extends string> = {
  step: K | null;
  phase: PayPhase | null;
};

/**
 * Steps someone else decides, told on the same beats as the payment's: the
 * first shows at once, inline, and the checkout folds FOLD_DELAY_MS later;
 * each step stays at least HOLD_MS so a fast one still reads. A provider
 * whose steps branch (a vendor's browser signing in or registering) cannot
 * be walked in a fixed order, so a step that arrives while another is held
 * waits, and only the latest one waiting is shown. `null` clears the status
 * at once and the checkout unfolds while active. While paused every target,
 * including null, waits for resume; hold time excludes the paused interval.
 */
export function createStepTimeline<K extends string>(input: {
  clock: Clock;
  onChange: (view: StepView<K>) => void;
}) {
  const { clock } = input;
  let target: K | null = null;
  let shown: K | null = null;
  let shownAt = 0;
  let phase: PayPhase = "inline";
  let stepTimer: TimerHandle | null = null;
  let foldTimer: TimerHandle | null = null;
  let disposed = false;
  let paused = false;
  let pausedAt = 0;

  const view = (): StepView<K> => ({
    step: shown,
    phase: shown !== null ? phase : null,
  });
  const clear = (timer: TimerHandle | null) => {
    if (timer !== null) clock.clearTimeout(timer);
    return null;
  };
  const fold = () => {
    foldTimer = clear(foldTimer);
    if (disposed || paused || shown === null) return;
    if (phase === "folding") return;
    foldTimer = clock.setTimeout(() => {
      phase = "folding";
      input.onChange(view());
    }, FOLD_DELAY_MS);
  };
  const show = (next: K | null) => {
    if (disposed || next === shown) return;
    const wasOn = shown !== null;
    shown = next;
    shownAt = clock.now();
    if (shown === null) {
      phase = "inline";
      foldTimer = clear(foldTimer);
    }
    input.onChange(view());
    if (!wasOn && shown !== null) fold();
  };
  const step = () => {
    stepTimer = clear(stepTimer);
    if (paused || disposed) return;
    if (target === null || shown === null) {
      show(target);
      return;
    }
    if (target === shown) return;
    const left = shownAt + HOLD_MS - clock.now();
    if (left <= 0) {
      show(target);
      return;
    }
    stepTimer = clock.setTimeout(() => {
      stepTimer = null;
      step();
    }, left);
  };

  return {
    view,
    setTarget(next: K | null) {
      if (disposed || next === target) return;
      target = next;
      step();
    },
    /** Stopped: no step or fold fires until `resume`. */
    pause() {
      if (disposed || paused) return;
      paused = true;
      pausedAt = clock.now();
      stepTimer = clear(stepTimer);
      foldTimer = clear(foldTimer);
    },
    /** Started again: carry on from where the status stands. */
    resume() {
      if (disposed || !paused) return;
      shownAt += clock.now() - pausedAt;
      paused = false;
      step();
      if (shown !== null && phase === "inline") fold();
    },
    dispose() {
      disposed = true;
      stepTimer = clear(stepTimer);
      foldTimer = clear(foldTimer);
    },
  };
}
